import React, { useState } from 'react';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import { IAbstractRecord } from '@models';
import { IConfirmation } from '@redux/confirmation/types';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import { useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useMutation } from '@apollo/client';
import { activateBusinessPartner, deactivateBusinessPartner } from './queries';
import { MAIN_THEME } from '@styles';
import { isEmpty } from '@utils/validationUtils';
import { toast } from 'react-toastify';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { useAppDispatch } from '@redux/hooks';

interface IBusinessPartnerStepper {
  businessPartnerInfo: IAbstractRecord;
  onSuccess: () => void;
}

const useStyles = makeStyles()(() => ({
  stepper: {
    backgroundColor: 'transparent',
    maxWidth: '90%',
    margin: '20px auto',
    '& .MuiStepLabel-root .Mui-active, .MuiStepLabel-root .Mui-completed': {
      color: MAIN_THEME.color.black,
    },

    '@media only screen and (min-width: 600px)': {
      maxWidth: '1300px',
    },
  },
}));

const BusinessPartnerStepper: React.FC<IBusinessPartnerStepper> = ({
  businessPartnerInfo,
  onSuccess,
}) => {
  const { id: currentBPId } = useParams<{ id: string }>();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const isBusinessPartnerActive = businessPartnerInfo?.status === 'active';
  const isBusinessPartnerNew = businessPartnerInfo?.status === 'new';
  const steps = isBusinessPartnerNew
    ? ['New', 'Active']
    : ['New', isBusinessPartnerActive ? 'Active' : 'Inactive'];
  const activeStep: Record<string, string> = {
    new: steps[0],
    active: steps[1],
    inactive: steps[1],
  };

  const [activateAction] = useMutation(activateBusinessPartner, {
    variables: {
      entityId: currentBPId,
    },
    errorPolicy: 'all',
  });
  const [deactivateAction] = useMutation(deactivateBusinessPartner, {
    variables: {
      entityId: currentBPId,
    },
    errorPolicy: 'all',
  });

  const [activateButtonState, setActivateButtonState] =
    useState<EnhancedButtonStatus>();
  const [deactivateButtonState, setDeactivateButtonState] =
    useState<EnhancedButtonStatus>();

  return (
    <EnhancedStepper
      activeStep={activeStep[businessPartnerInfo?.status]}
      steps={steps}
      className={classes.stepper}
      showStepperButton
      buttonTitle={isBusinessPartnerActive ? 'Deactivate' : 'Activate'}
      buttonState={
        isBusinessPartnerActive ? activateButtonState : deactivateButtonState
      }
      buttonOnClick={async () => {
        if (isBusinessPartnerActive) {
          const confirmation: IConfirmation = {
            open: true,
            title: 'Deactivate',
            message: `Are you sure you want to deactivate this business partner?`,
            callback: async () => {
              try {
                setDeactivateButtonState('loading');
                const result = await deactivateAction();
                if (isEmpty(result.errors)) {
                  setDeactivateButtonState('success');
                  onSuccess();
                  toast.success(
                    <ToastSuccessMessage>
                      Business Partner successfully deactivated.
                    </ToastSuccessMessage>
                  );
                } else {
                  setDeactivateButtonState(undefined);
                  toast.error(
                    <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
                  );
                }
              } finally {
                setTimeout(() => {
                  setDeactivateButtonState(undefined);
                }, 1000);
              }
            },
            submitButtonText: 'Yes',
            cancelButtonText: 'No',
          };
          dispatch(OpenConfirmationAction(confirmation));
        } else {
          const confirmation: IConfirmation = {
            open: true,
            title: 'Activate',
            message: `Are you sure you want to activate this business partner?`,
            callback: async () => {
              try {
                setActivateButtonState('loading');
                const result = await activateAction();
                if (isEmpty(result.errors)) {
                  setActivateButtonState('success');
                  onSuccess();
                  toast.success(
                    <ToastSuccessMessage>
                      {'Business Partner successfully activated.'}
                    </ToastSuccessMessage>
                  );
                } else {
                  setActivateButtonState(undefined);
                  toast.error(
                    <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
                  );
                }
              } finally {
                setTimeout(() => {
                  setActivateButtonState(undefined);
                }, 1000);
              }
            },
            submitButtonText: 'Yes',
            cancelButtonText: 'No',
          };
          dispatch(OpenConfirmationAction(confirmation));
        }
      }}
    />
  );
};

export default BusinessPartnerStepper;
